import {datadogLogs} from '@datadog/browser-logs';
import {type LogsGlobal} from '@datadog/browser-logs';
import {once} from 'lodash';

import config from '../../config';
import {
  DATADOG_CLIENT_TOKEN,
  DATADOG_SITE,
  DATADOG_UI_SERVICE,
} from './constants';
import {useDatadogContext} from './DatadogContext';

/**
 * Initializes Datadog LOGS with configuration for session sampling and error forwarding.
 * This function is wrapped with `once()` to ensure it only runs once.
 * Session sample rate is 100% in debug mode, otherwise 25% of sessions.
 */
export const initDatadogLogs = once(function initDatadogLogs() {
  datadogLogs.init({
    clientToken: DATADOG_CLIENT_TOKEN,
    env: window.CONFIG?.ENVIRONMENT_NAME,
    version: window.CONFIG?.GIT_TAG,
    site: DATADOG_SITE,
    forwardErrorsToLogs: true,
    // v7 decoupled console forwarding from forwardErrorsToLogs; keep forwarding
    // console.error to preserve pre-v7 behavior.
    forwardConsoleLogs: ['error'],
    sessionSampleRate: config.DATADOG_DEBUG ? 100 : 25, // only send logs for X% of _sessions_
    service: DATADOG_UI_SERVICE,
  });
});

/**
 * Returns the Datadog Logs global instance for logging operations.
 *
 * @returns The Datadog Logs global instance
 */
export function getDatadogLogs(): LogsGlobal {
  return datadogLogs;
}

/**
 * React hook to access the Datadog Logs instance.
 *
 * @returns The Datadog Logs global instance
 */
export function useDatadogLogs(): LogsGlobal {
  return useDatadogContext().logs;
}
